import React, { Suspense, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '@/components/card';
import Title from '@/components/title';
import { v4 as uuidv4 } from 'uuid';
import {
  IStockMatchingReportReq,
  IStockMatchingReportRes,
} from '@/interfaces/report';
import { Button, Table, TableColumnsType } from 'antd';
import { ReportMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { AxiosResponse } from 'axios';
import { getStockMatching } from '@/adapters/report';
import { formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import ImportExcel from './components/import-excel';
import ExcelExport from './components/export-report';
import { ExpandAltOutlined, ExpandOutlined } from '@ant-design/icons';
import _, { sumBy } from 'lodash';
import StockMovementModal from '@/components/stock-movement/stock-movement-modal';
import { IDataModal } from '@/interfaces/dataModal';
import { useAuth } from '@/contexts/AuthContext';
// import BusinessUnitMultiSelector from '@/components/selector/business-unit-multi-selector';

const _stockModal: IDataModal<IStockMatchingReportRes> = {
  open: false,
};

const StockMatchingReport = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  console.log(userInfo);
  const [criteria] = useState<IStockMatchingReportReq>({
    data: [],
  });
  const [hidden, setHidden] = useState<boolean>(true);
  const [groups, setGroups] = useState<string[]>([]);

  const [movementModal, setMovementModal] = useState<
    IDataModal<IStockMatchingReportRes>
  >({ ..._stockModal });

  const { data, isLoading } = useQuery<
    AxiosResponse<IStockMatchingReportRes[]>,
    Error
  >({
    queryKey: ['search-stock-matching-report', criteria],
    queryFn: async () => await getStockMatching(criteria),
  });

  const columns: TableColumnsType<IStockMatchingReportRes> = [
    {
      title: 'ITEM NO',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 160,
      className: 'font-bold',
      render: _ => (
        <div className={twMerge(!_ && 'bg-red-100')}>{_ || '-'}</div>
      ),
    },
    {
      title: 'BU NAMES',
      dataIndex: 'bUs',
      key: 'bUs',
      width: 120,
      className: 'font-bold',
      filters: [
        { text: 'KA', value: 'KA' },
        { text: 'KAQ', value: 'KAQ' },
        { text: 'KN', value: 'KN' },
        { text: 'KB', value: 'KB' },
        { text: 'NULL', value: '' },
      ],
      onFilter: (value, record) => {
        return record.bUs?.split(',').includes(value as string) || false;
      },
      // defaultFilteredValue: userInfo?.buName
      //   ? [userInfo.buName]
      //   : ['KA', 'KAQ', 'KN', ''],
    },
    {
      title: 'ITEM NAME',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'KEMIN LOT.',
      dataIndex: 'keminLotNumber',
      key: 'keminLotNumber',
      width: 120,
      align: 'center',
      render: (_, rec) => (
        <div
          className={twMerge(
            !_ && 'bg-red-100',
            _ && 'underline underline-offset-2 cursor-pointer',
          )}
          onClick={() =>
            setMovementModal({
              open: true,
              item: rec,
            })
          }
        >
          {_ || '-'}
        </div>
      ),
    },
    {
      title: 'PIONEER LOT.',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      width: 120,
      align: 'center',
      render: (_, rec) => (
        <div
          className={twMerge(
            !_ && 'bg-red-100',
            _ && 'underline underline-offset-2 cursor-pointer',
          )}
          onClick={() =>
            setMovementModal({
              open: true,
              item: rec,
            })
          }
        >
          {_ || '-'}
        </div>
      ),
    },
    {
      title: 'KEMIN QTY',
      dataIndex: 'keminQuantity',
      key: 'keminQuantity',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_ || 0, 2),
    },
    {
      title: 'PIONEER QTY',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_ || 0, 2),
      hidden: hidden,
    },
    {
      title: 'ACTUAL QTY',
      dataIndex: 'pioneerQuantity',
      key: 'pioneerQuantity',
      width: 100,
      align: 'right',
      render: _ => formatNumber(_ || 0, 2),
    },
    {
      title: 'รอส่ง',
      dataIndex: 'pioneerReserveQuantity',
      key: 'pioneerReserveQuantity',
      width: 100,
      align: 'right',
      render: _ => {
        return (
          <div className={twMerge(_ > 0 && 'text-orange-600')}>
            {formatNumber(_ || 0, 2)}
          </div>
        );
      },
    },
    {
      title: 'WH. DIFF',
      dataIndex: 'wh-diff',
      key: 'wh-diff',
      width: 140,
      align: 'right',
      className: 'font-bold',
      filters: [{ text: 'DIFF', value: 0 }],
      onFilter: (value, record) =>
        Number(record.pioneerQuantity) - Number(record.quantity) != value,
      // defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        Number(a.pioneerQuantity) -
        Number(a.quantity) -
        (Number(b.pioneerQuantity) - Number(b.quantity)),
      render: (_, rec) => {
        const diff =
          Number(rec.pioneerQuantity || 0) - Number(rec.quantity || 0);
        return (
          <div
            className={twMerge(
              diff < 0 && 'text-red-500',
              diff > 0 && 'text-green-600',
            )}
          >
            {formatNumber(diff, 2)}
          </div>
        );
      },
      hidden: hidden,
    },
    {
      title: 'DIFF',
      dataIndex: 'diff',
      key: 'diff',
      width: 100,
      align: 'right',
      className: 'font-bold',
      filters: [{ text: 'DIFF', value: 0 }],
      onFilter: (value, record) =>
        Number(record.keminQuantity) - Number(record.pioneerQuantity) != value,
      // defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        Number(a.keminQuantity) -
        Number(a.pioneerQuantity) -
        Number(a.pioneerReserveQuantity) -
        (Number(b.keminQuantity) -
          Number(b.pioneerQuantity) -
          Number(b.pioneerReserveQuantity || 0)),
      render: (_, rec) => {
        const diff =
          Number(rec.keminQuantity || 0) -
          Number(rec.pioneerQuantity || 0) -
          Number(rec.pioneerReserveQuantity || 0);
        return (
          <div
            className={twMerge(
              diff < 0 && 'text-red-500',
              diff > 0 && 'text-green-600',
            )}
          >
            {formatNumber(diff, 2)}
          </div>
        );
      },
    },
  ];

  const groupedByProduct: IStockMatchingReportRes[] = Object.values(
    _.groupBy(data?.data || [], 'productCode'),
  ).map((c, i) => {
    const map: IStockMatchingReportRes = {
      key: i.toString(),
      productCode: c[0].productCode,
      productName: c[0].productName,
      keminLotNumber: '_',
      lotNumber: '_',
      bUs: c[0].bUs,
      keminQuantity: sumBy(c, c => Number(c.keminQuantity)),
      quantity: sumBy(c, c => Number(c.quantity)),
      pioneerQuantity: sumBy(c, c => Number(c.pioneerQuantity)),
      pioneerReserveQuantity: sumBy(c, c => Number(c.pioneerReserveQuantity)),
      children: c.map(c => {
        return { ...c, key: uuidv4(), bUs: c.bUs || '' };
      }),
    } as IStockMatchingReportRes;
    return map;
  });

  console.log(groupedByProduct);

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Stock Matching Report">
          <div className="flex flex-row gap-2">
            <ImportExcel
              onImported={function (): void {
                console.log();
              }}
            />
            <ExcelExport data={data?.data || []} />

            {groups.length ? (
              <Button onClick={() => setGroups([])} icon={<ExpandOutlined />} />
            ) : (
              <Button
                onClick={() =>
                  setGroups(groupedByProduct.map(c => c.key || ''))
                }
                icon={<ExpandAltOutlined />}
              />
            )}
            <Button onClick={() => setHidden(!hidden)} />
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <Table<IStockMatchingReportRes>
            rowKey={c => c.key || ''}
            columns={columns}
            dataSource={groupedByProduct || []}
            bordered
            size="small"
            loading={isLoading}
            locale={{
              emptyText: 'ไม่พบรายการ',
            }}
            scroll={{ x: 1200 }}
            rowClassName={record => (record.children ? '' : 'bg-blue-50')}
            pagination={false}
            sticky={{ offsetHeader: 60 }}
            expandable={{
              expandedRowKeys: groups,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setGroups(prev => [...prev, record.key || '']);
                } else {
                  setGroups(prev => prev.filter(key => key !== record.key));
                }
              },
            }}
          />
        </div>
        <Suspense>
          {movementModal.item && (
            <StockMovementModal
              handleCancel={() => setMovementModal({ ..._stockModal })}
              open={movementModal.open}
              stockId={0}
              productId={Number(movementModal.item?.productId)}
              lotNumber={String(
                movementModal.item?.lotNumber ||
                  movementModal.item?.keminLotNumber,
              )}
            />
          )}
        </Suspense>
      </Card>
    </>
  );
};

export default StockMatchingReport;
